import { ThemeEvents, CartAddEvent } from '@theme/events';

const JUST_ADDED_CLASS = 'yossoa-cart-just-added';
const CARD_ADDING_CLASS = 'yossoa-card-adding';
const CARD_BUTTON_CLASS = 'yossoa-card-cart-button';
const ADDED_LABEL = 'Added';
const ADD_LABEL = 'Add to cart';

const TEXT_REPLACEMENTS = [
  [/您的購物車是空的/g, 'Your cart is empty'],
  [/購物車總金額/g, 'Cart total'],
  [/購物車/g, 'Cart'],
  [/已有帳號？/g, 'Have an account?'],
  [/登入/g, 'Log in'],
  [/以加速結帳。/g, 'to check out faster.'],
  [/繼續購物/g, 'Continue shopping'],
  [/價格/g, 'Price'],
  [/數量/g, 'Quantity'],
  [/商品總數/g, 'Line total'],
  [/產品圖片/g, 'Product image'],
  [/產品資訊/g, 'Product information'],
  [/折扣/g, 'Discount'],
  [/估計總金額/g, 'Estimated total'],
  [/結帳時計算稅額和運費。/g, 'Taxes and shipping calculated at checkout.'],
  [/結帳/g, 'Checkout'],
  [/移除/g, 'Remove'],
  [/增加/g, 'Increase'],
  [/減少/g, 'Decrease'],
  [/關閉對話/g, 'Close dialog'],
  [/電子郵件地址/g, 'Email address'],
  [/註冊/g, 'Sign up'],
];

const normalizePath = (path) => path.replace(/\/$/, '') || '/';

const normalizeMoneyText = (text) => text.replace(/HK\$\s*([0-9,.]+)\s*HKD/g, (_match, amount) => `HK$${amount}`);

const translateCartText = (text) => {
  let translated = normalizeMoneyText(text);

  TEXT_REPLACEMENTS.forEach(([pattern, replacement]) => {
    translated = translated.replace(pattern, replacement);
  });

  return translated;
};

const normalizeCartDrawer = () => {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return;

  document.documentElement.lang = 'en';

  const walker = document.createTreeWalker(drawer, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);

  textNodes.forEach((node) => {
    const nextValue = translateCartText(node.nodeValue || '');
    if (nextValue !== node.nodeValue) node.nodeValue = nextValue;
  });

  drawer.querySelectorAll('[aria-label], [title], [placeholder], [value]').forEach((element) => {
    ['aria-label', 'title', 'placeholder', 'value'].forEach((attribute) => {
      const value = element.getAttribute(attribute);
      if (!value) return;

      const nextValue = translateCartText(value);
      if (nextValue !== value) element.setAttribute(attribute, nextValue);
    });
  });
};

const openCartDrawer = () => {
  if (normalizePath(window.location.pathname) === '/cart') return;

  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return;

  if (typeof drawer.open === 'function') {
    drawer.open();
  } else {
    drawer.querySelector('[data-testid="cart-drawer-trigger"]')?.click();
  }

  normalizeCartDrawer();
  document.documentElement.classList.add(JUST_ADDED_CLASS);
  window.setTimeout(() => document.documentElement.classList.remove(JUST_ADDED_CLASS), 700);
};

const getVariantId = (card) => {
  const productLink = card.querySelector('a[href*="/products/"][href*="variant="]');
  if (!productLink) return null;

  try {
    const url = new URL(productLink.getAttribute('href'), window.location.origin);
    return url.searchParams.get('variant');
  } catch (_error) {
    return null;
  }
};

const getCartSectionIds = () => {
  const ids = new Set();

  document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
    if (component.dataset.sectionId) ids.add(component.dataset.sectionId);
  });

  return [...ids];
};

const addVariantToCart = async (variantId, card) => {
  const sectionIds = getCartSectionIds();
  const payload = {
    id: Number(variantId),
    quantity: 1,
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  };

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json();

  if (data.status) throw new Error(data.description || data.message || 'Unable to add item to cart.');

  const cart = await fetch('/cart.js').then((cartResponse) => cartResponse.json());
  document.dispatchEvent(new CartAddEvent(cart, `yossoa-card-${variantId}`, {
    source: 'yossoa-card-button',
    itemCount: 1,
    productId: card.dataset.productId,
    sections: data.sections,
  }));
};

const setButtonState = (button, state) => {
  button.dataset.state = state;
  button.textContent = state === 'added' ? ADDED_LABEL : ADD_LABEL;
  button.disabled = state === 'loading';
};

const handleCardButtonClick = async (event) => {
  const button = event.target instanceof Element ? event.target.closest(`.${CARD_BUTTON_CLASS}`) : null;
  if (!button) return;

  event.preventDefault();
  event.stopPropagation();

  const card = button.closest('product-card, product-card-link');
  const variantId = button.dataset.variantId;
  if (!card || !variantId) return;

  card.classList.add(CARD_ADDING_CLASS);
  setButtonState(button, 'loading');

  try {
    await addVariantToCart(variantId, card);
    setButtonState(button, 'added');
  } catch (error) {
    console.error(error);
    setButtonState(button, 'idle');
  } finally {
    window.setTimeout(() => {
      card.classList.remove(CARD_ADDING_CLASS);
      setButtonState(button, 'idle');
    }, 1100);
  }
};

const enhanceProductCards = () => {
  document.querySelectorAll('product-card, product-card-link').forEach((card) => {
    if (card.querySelector(`.${CARD_BUTTON_CLASS}`)) return;

    const variantId = getVariantId(card);
    if (!variantId) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = CARD_BUTTON_CLASS;
    button.dataset.variantId = variantId;
    button.textContent = ADD_LABEL;
    button.setAttribute('aria-label', `${ADD_LABEL}: ${card.textContent.trim().replace(/\s+/g, ' ').slice(0, 80)}`);

    card.append(button);
  });
};

let enhanceQueued = false;
const scheduleEnhanceProductCards = () => {
  if (enhanceQueued) return;
  enhanceQueued = true;

  window.requestAnimationFrame(() => {
    enhanceQueued = false;
    enhanceProductCards();
    normalizeCartDrawer();
  });
};

document.addEventListener('click', handleCardButtonClick);
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element
    ? event.target.closest('button[name="add"], .quick-add__button')
    : null;
  const card = button?.closest('product-card, .product-card');

  if (!card) return;

  card.classList.add(CARD_ADDING_CLASS);
  window.setTimeout(() => card.classList.remove(CARD_ADDING_CLASS), 900);
});

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = event.detail || {};
  const data = detail.data || {};

  normalizeCartDrawer();

  if (data.didError || data.source === 'cart-items-component') return;

  window.requestAnimationFrame(openCartDrawer);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    enhanceProductCards();
    normalizeCartDrawer();
  }, { once: true });
} else {
  enhanceProductCards();
  normalizeCartDrawer();
}

new MutationObserver(scheduleEnhanceProductCards).observe(document.documentElement, {
  childList: true,
  subtree: true,
  characterData: true,
});
